import { useEffect, useId, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BIBLE_BOOKMARKS_ENABLED,
  BIBLE_READER_V2_ENABLED,
  PRAY_MODE_ENABLED,
  SCRIPTURE_JOURNAL_ENABLED,
} from '@/lib/feature-flags';
import { HL_DOT_HEX, coerceFamily, coerceSublabel } from '@/lib/highlight-utils';
import {
  JOURNAL_SEARCH_LIMITS,
  searchJournalIndex,
  type JournalSearchResult,
  type VisibleJournalSearchLabel,
} from '@/lib/journal-search';
import { formatRelativeTime } from '@/lib/prayer-utils';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useJournalBibleSearchText } from '@/hooks/useJournalBibleSearchText';
import { useJournalSearchIndex } from '@/hooks/useJournalSearchIndex';
import { useJournalSources } from '@/hooks/useJournalSources';
import {
  allEntries,
  bookSummaries,
  bookHighlightFamilies,
  topBooks,
  totalCounts,
  mostRecentEntry,
  entryBookSlug,
  entryChapter,
  entryReference,
  JOURNAL_PAGE_SIZE,
  familyCounts,
  type JournalEntry,
} from '@/lib/journal-aggregate';
import JournalWalk from '@/components/JournalWalk/JournalWalk';
import JournalPagination from '@/components/JournalPagination/JournalPagination';
import JournalSourceState from '@/components/JournalSourceState/JournalSourceState';
import BibleThemePicker from '@/components/BibleThemePicker/BibleThemePicker';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import { useSettingsStore } from '@/lib/settings-store';
import type { HighlightFamily } from '@/types/highlight';
import styles from './BibleBookmarksPage.module.css';

/**
 * Scripture Journal home at /bible/bookmarks (canon-spatial redesign Phase 2,
 * §6). A leaf route (rule #10 overlay/route split: own back Link, native browser
 * back, NO NavStack layer). REPLACES the old flat Recent/By-book feed with
 * "Your Bible at a glance": a count breakdown, a "Pick up where you left off"
 * jump-back button, "Books you return to most" cards, the full marked-book list
 * with an intensity bar, and the existing search. The journal is organized by
 * WHERE in Scripture a mark lives (the canon), not by when it was saved.
 *
 * Structure comes from the pure journal-aggregate leaf. A nonempty search
 * lazily resolves only the distinct BSB books needed by indexable highlights;
 * note and prayer search remains usable when public Scripture is unavailable.
 * Sources flow through useJournalSources so disabled sources collapse to [] and
 * the empty state never flashes before every enabled IDB source hydrates.
 *
 * Search spans all entries; a hit routes to its canon home: a verse note → its
 * reflection thread (where it's read/edited, D5), everything else (legacy
 * whole-chapter note, highlight, prayer) → the passage page (D2/D4). Results use
 * exact 50-row paging, so the DOM never holds the full result set. Meaning
 * categories and the prayed card sit after the jump-back so the journal's
 * filing system is visible from the home (Scripture Journal UX arc Chunk 5).
 */

const TOP_BOOKS_N = 4;
const SEARCH_RESULT_EXCERPT_CHARACTERS = 320;
const SEARCH_RESULT_EXCERPT_INPUT_CHARACTERS = 2_048;
const EMPTY_VISIBLE_SEARCH_LABELS: readonly VisibleJournalSearchLabel[] =
  Object.freeze([]);

function boundedSearchExcerpt(value: string): string {
  const compact = value
    .slice(0, SEARCH_RESULT_EXCERPT_INPUT_CHARACTERS)
    .replace(/\s+/gu, ' ')
    .trim();
  const characters = Array.from(compact);
  if (characters.length <= SEARCH_RESULT_EXCERPT_CHARACTERS) return compact;
  return `${characters
    .slice(0, SEARCH_RESULT_EXCERPT_CHARACTERS - 1)
    .join('')
    .trimEnd()}…`;
}

function trustedVerseKey(bookSlug: string, chapter: number, verse: number): string {
  return `${bookSlug}:${chapter}:${verse}`;
}

// A search hit's canon destination: a verse-bearing note → its reflection
// thread; everything else (legacy whole-chapter note, highlight, prayer) → the
// passage page where the mark lives in context.
function searchRowTo(e: JournalEntry): string {
  if (e.kind === 'note' && e.bookmark.anchor.verses.length > 0) {
    const verse = e.bookmark.anchor.verses[0];
    return `/bible/bookmarks/${e.bookmark.anchor.bookSlug}/${e.bookmark.anchor.chapter}/${verse}`;
  }
  return `/bible/bookmarks/book/${entryBookSlug(e)}/${entryChapter(e)}`;
}

export default function BibleBookmarksPage() {
  const { i18n, t } = useTranslation();
  const { sources, state: sourceState, ready, retry } = useJournalSources();
  const bibleTheme = useSettingsStore((s) => s.bibleTheme);
  // App notice stack (v0.2.0 finishing): an in-flow spacer at the top of the
  // page (above the Back link and the theme picker, so the picker's menu also
  // opens below the notices) keeps the page below the fixed notices at the
  // top; 0 while no notice shows. Each render path mounts its own spacer with
  // the same callback ref.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();

  const [query, setQuery] = useState('');
  const [searchActivated, setSearchActivated] = useState(false);
  const debouncedQuery = useDebouncedValue(query, 150);
  const liveTrimmed = query.trim();
  const trimmed = debouncedQuery.trim();
  const isSearching = liveTrimmed !== '';
  const queryPending = isSearching && liveTrimmed !== trimmed;
  const searchEnabled = ready && isSearching && !queryPending;

  const [searchPage, setSearchPage] = useState(0);
  const searchStatusId = useId();
  const searchResultsId = useId();

  useEffect(() => {
    const prior = document.title;
    document.title = t('bookmark.journalTitle');
    return () => {
      document.title = prior;
    };
  }, [t]);

  const entries = useMemo(() => allEntries(sources), [sources]);
  // allEntries is grouped notes → highlights → prayers. Mirror the leaf's hard
  // first-N entry cap so BSB and localized-label preparation never works on a
  // highlight that the bounded index cannot accept.
  const searchableHighlights = useMemo(
    () =>
      sources.highlights.slice(
        0,
        Math.max(0, JOURNAL_SEARCH_LIMITS.maxEntries - sources.bookmarks.length),
      ),
    [sources.bookmarks.length, sources.highlights],
  );
  const authOwnerId = getAuthUserId();
  const authEpoch = getAuthEpoch();
  const bibleSearchScope = useMemo(
    () => ({ authEpoch, authOwnerId, highlights: searchableHighlights }),
    [authEpoch, authOwnerId, searchableHighlights],
  );
  const searchLocale = i18n.resolvedLanguage ?? i18n.language;
  const indexScope = useMemo(
    () => ({ authEpoch, authOwnerId, entries, locale: searchLocale }),
    [authEpoch, authOwnerId, entries, searchLocale],
  );
  const { state: bibleSearchText, retry: retryBibleSearchText } =
    useJournalBibleSearchText(searchableHighlights, {
      enabled: searchEnabled,
      scope: bibleSearchScope,
    });
  const visibleSearchLabels = useMemo<readonly VisibleJournalSearchLabel[]>(
    () => {
      if (!ready || !searchActivated) return EMPTY_VISIBLE_SEARCH_LABELS;
      return searchableHighlights.map((highlight) => {
        const family = coerceFamily(highlight.family) ?? 'plain';
        const sublabel = coerceSublabel(family, highlight.sublabel);
        const familyName = t(`bible.hlFamily.${family}`);
        const sublabelName = sublabel
          ? t(`bible.hlSublabel.${sublabel}`)
          : '';
        return {
          kind: 'highlight',
          sourceId: highlight.id,
          text: sublabelName ? `${familyName} ${sublabelName}` : familyName,
        };
      });
    },
    [ready, searchActivated, searchableHighlights, t],
  );
  const searchIndex = useJournalSearchIndex(
    entries,
    bibleSearchText.rows,
    visibleSearchLabels,
    {
      enabled: searchEnabled && bibleSearchText.kind !== 'loading',
      scope: indexScope,
    },
  );
  const counts = useMemo(() => totalCounts(sources), [sources]);
  const books = useMemo(() => bookSummaries(sources), [sources]);
  const top = useMemo(() => topBooks(sources, TOP_BOOKS_N), [sources]);
  const recent = useMemo(() => mostRecentEntry(sources), [sources]);
  const families = useMemo(
    () => familyCounts(sources.highlights),
    [sources.highlights],
  );
  const maxBookCount = useMemo(
    () => books.reduce((m, b) => Math.max(m, b.markCount), 1),
    [books],
  );
  // Each book's dominant highlight family (null when it holds only notes/prayers
  // or v2 is off), so the "Books you return to most" cards and the browse rows
  // wear the color of what mostly lives in them (rich color, rule #14 via
  // HL_DOT_HEX). Gold is the fallback accent.
  const domByBook = useMemo(() => {
    const m = new Map<string, HighlightFamily | null>();
    for (const b of books) {
      m.set(b.slug, bookHighlightFamilies(sources, b.slug)[0]?.family ?? null);
    }
    return m;
  }, [books, sources]);
  const accentFor = (slug: string): string => {
    const fam = domByBook.get(slug);
    return fam ? HL_DOT_HEX[fam] : 'var(--reader-accent)';
  };

  const searchResponse = useMemo(
    () =>
      searchEnabled && searchIndex.kind === 'ready'
        ? searchJournalIndex(searchIndex.index, trimmed, {
            limit: JOURNAL_SEARCH_LIMITS.maxResults,
          })
        : null,
    [searchEnabled, searchIndex.index, searchIndex.kind, trimmed],
  );
  const searchResults = useMemo<readonly JournalSearchResult[]>(
    () => searchResponse?.results ?? [],
    [searchResponse],
  );
  const trustedVerseTextByKey = useMemo(
    () =>
      new Map(
        bibleSearchText.rows.map((row) => [
          trustedVerseKey(row.bookSlug, row.chapter, row.verse),
          row.text,
        ]),
      ),
    [bibleSearchText.rows],
  );
  const searchPageCount = Math.max(
    1,
    Math.ceil(searchResults.length / JOURNAL_PAGE_SIZE),
  );
  const boundedSearchPage = Math.min(searchPage, searchPageCount - 1);
  const searchPageStart = boundedSearchPage * JOURNAL_PAGE_SIZE;
  const searchBusy =
    isSearching &&
    (queryPending ||
      !ready ||
      searchIndex.kind === 'building' ||
      bibleSearchText.kind === 'loading');
  let searchStatus = '';
  if (isSearching && ready) {
    if (queryPending) searchStatus = t('bookmark.searchUpdating');
    else if (bibleSearchText.kind === 'loading') {
      searchStatus = t('bookmark.searchAddingScripture');
    } else if (searchIndex.kind === 'failed') {
      searchStatus = t('bookmark.searchUnavailable');
    } else if (searchIndex.kind !== 'ready') {
      searchStatus = t('bookmark.searchPreparing');
    } else if (bibleSearchText.kind === 'partial') {
      searchStatus = t('bookmark.searchScripturePartial');
    } else if (searchResults.length === 0) {
      searchStatus = t('bookmark.searchNoMatch');
    } else {
      searchStatus = t('bookmark.searchResultCount', {
        count: searchResults.length,
      });
    }
  }
  const bibleSearchRetryable =
    bibleSearchText.kind === 'partial' &&
    bibleSearchText.failedBookCount > 0;

  if (!SCRIPTURE_JOURNAL_ENABLED)
    return <Navigate to="/bible" replace />;

  const renderSearchRow = (result: JournalSearchResult) => {
    const e = result.entry;
    const to = searchRowTo(e);
    const timeAgo = formatRelativeTime(new Date(e.kind === 'note' ? e.bookmark.createdAt : e.kind === 'highlight' ? e.highlight.createdAt : e.prayer.createdAt).getTime(), t);
    const resultData = {
      'data-search-match-kind': result.matchKind,
      'data-search-match-fields': result.matchedFields.join(','),
    };

    if (e.kind === 'note') {
      const b = e.bookmark;
      const isLegacy = b.anchor.verses.length === 0;
      return (
        <li
          key={`note-${b.id}`}
          className={styles.rowItem}
          {...resultData}
        >
          <Link to={to} className={styles.row}>
            <div className={styles.rowTop}>
              <span className={styles.rowRef}>{b.anchor.reference}</span>
              {isLegacy && (
                <span className={styles.legacyBadge}>
                  {t('bookmark.wholeChapterLabel')}
                </span>
              )}
              <span className={styles.rowTime}>{timeAgo}</span>
            </div>
            {b.note ? (
              <p className={styles.rowNote}>{boundedSearchExcerpt(b.note)}</p>
            ) : (
              <p className={styles.rowEmptyNote}>
                {t('bookmark.emptyNotePlaceholder')}
              </p>
            )}
          </Link>
        </li>
      );
    }

    if (e.kind === 'prayer') {
      const p = e.prayer;
      const a = p.scriptureAnchor!;
      return (
        <li key={`pr-${p.id}`} className={styles.rowItem} {...resultData}>
          <Link to={to} className={styles.row}>
            <div className={styles.rowTop}>
              <span className={styles.rowRef}>{a.reference}</span>
              <span className={styles.rowTime}>{timeAgo}</span>
            </div>
            <span className={styles.prayerLabel}>
              {t('bookmark.prayerLabel')}
            </span>
            {p.rawText ? (
              <p className={styles.rowNote}>
                {boundedSearchExcerpt(p.rawText)}
              </p>
            ) : null}
          </Link>
        </li>
      );
    }

    const hl = e.highlight;
    const fam = coerceFamily(hl.family) ?? 'plain';
    const sub = coerceSublabel(fam, hl.sublabel);
    const label = sub
      ? `${t(`bible.hlFamily.${fam}`)} · ${t(`bible.hlSublabel.${sub}`)}`
      : t(`bible.hlFamily.${fam}`);
    const matchingScripture = result.matchedFields.includes('bsb-scripture')
      ? trustedVerseTextByKey.get(
          trustedVerseKey(hl.bookSlug, hl.chapter, hl.verse),
        )
      : undefined;
    return (
      <li key={`hl-${hl.id}`} className={styles.rowItem} {...resultData}>
        <Link to={to} className={styles.row}>
          <div className={styles.rowTop}>
            <span className={styles.rowRef}>{hl.reference}</span>
            <span className={styles.rowTime}>{timeAgo}</span>
          </div>
          <p className={styles.hlLabel}>
            <span
              className={styles.hlDot}
              style={{ backgroundColor: HL_DOT_HEX[fam] }}
              aria-hidden="true"
            />
            {label}
          </p>
          {matchingScripture && (
            <p className={styles.scriptureMatch}>
              <span className={styles.scriptureMatchLabel}>
                {t('bookmark.searchScriptureMatchLabel')}
              </span>{' '}
              {boundedSearchExcerpt(matchingScripture)}
            </p>
          )}
        </Link>
      </li>
    );
  };

  return (
    <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
      <div className={styles.readerBg} aria-hidden="true" />
      <div className={styles.container} data-bible-theme={bibleTheme}>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />
      <div className={styles.topBar}>
        <Link to="/bible" className={styles.backLink}>
          {t('bible.back')}
        </Link>
        <div className={styles.headerActions}>
          <BibleThemePicker />
        </div>
      </div>
      <h1 className={styles.title}>{t('bookmark.journalTitle')}</h1>

      <div className={styles.searchRow}>
        <div className={styles.searchWrap}>
          <svg
            className={styles.searchIcon}
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            className={styles.searchInput}
            value={query}
            onChange={(e) => {
              const nextQuery = e.target.value;
              setQuery(nextQuery);
              if (nextQuery.trim()) setSearchActivated(true);
              setSearchPage(0);
            }}
            placeholder={t('bookmark.searchPlaceholder')}
            aria-label={t('bookmark.searchPlaceholder')}
            aria-controls={searchResultsId}
            aria-describedby={searchStatusId}
            aria-busy={searchBusy}
            inputMode="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
          />
          {query && (
            <button
              type="button"
              className={styles.searchClear}
              onClick={() => {
                setQuery('');
                setSearchPage(0);
              }}
              aria-label={t('bibleSearch.clear')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className={styles.searchStatusRow}>
        <p
          id={searchStatusId}
          className={styles.searchStatus}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {searchStatus}
        </p>
        {isSearching && !queryPending && searchIndex.kind === 'failed' && (
          <button
            type="button"
            className={styles.searchRetry}
            onClick={searchIndex.retry}
          >
            {t('bookmark.tryAgain')}
          </button>
        )}
        {isSearching && !queryPending && bibleSearchRetryable && (
          <button
            type="button"
            className={styles.searchRetry}
            onClick={retryBibleSearchText}
          >
            {t('bookmark.searchScriptureRetry')}
          </button>
        )}
      </div>

      <div
        id={searchResultsId}
        className={styles.searchResultsRegion}
        role={isSearching ? 'region' : undefined}
        aria-label={
          isSearching ? t('bookmark.searchResultsLabel') : undefined
        }
        aria-busy={searchBusy}
      >
      {!ready ? (
        <JournalSourceState state={sourceState} onRetry={retry} />
      ) : isSearching ? (
        !queryPending && searchIndex.kind === 'ready' ? (
          <>
            <div className={styles.searchNotices}>
              {searchResponse?.resultsTruncated && (
                <p className={styles.statusLine}>
                  {t('bookmark.searchBestResults', {
                    count: searchResults.length,
                  })}
                </p>
              )}
              {searchResponse?.corpusTruncated && (
                <p className={styles.statusLine}>
                  {t('bookmark.searchCoverageLimited')}
                </p>
              )}
              {searchResponse?.metrics.workTruncated && (
                <p className={styles.statusLine}>
                  {t('bookmark.searchWorkLimited')}
                </p>
              )}
              {searchResponse?.metrics.queryTruncated && (
                <p className={styles.statusLine}>
                  {t('bookmark.searchQueryLimited')}
                </p>
              )}
            </div>
            {searchResults.length > 0 && (
              <>
                <ul className={styles.list}>
                  {searchResults
                    .slice(searchPageStart, searchPageStart + JOURNAL_PAGE_SIZE)
                    .map(renderSearchRow)}
                </ul>
                <JournalPagination
                  page={boundedSearchPage}
                  totalItems={searchResults.length}
                  onPageChange={setSearchPage}
                />
              </>
            )}
          </>
        ) : null
      ) : entries.length === 0 ? (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>{t('bookmark.listEmpty')}</p>
          <p className={styles.emptyHint}>
            {t(
              BIBLE_READER_V2_ENABLED
                ? 'bookmark.listEmptyHintV2'
                : BIBLE_BOOKMARKS_ENABLED
                  ? 'bookmark.listEmptyHint'
                  : 'bookmark.listEmptyHintPray',
            )}
          </p>
        </div>
      ) : (
        <>
          {recent && (
            <Link
              to={`/bible/bookmarks/book/${entryBookSlug(recent)}/${entryChapter(recent)}`}
              className={styles.jumpBack}
            >
              <span className={styles.jumpBackLabel}>
                {t('bookmark.jumpBackInLabel')}
              </span>
              <span className={styles.jumpBackEntry}>
                {t(`bookmark.recentEntryType.${recent.kind}`)}:{' '}
                {entryReference(recent)}
              </span>
              <span className={styles.jumpBackTime}>
                {formatRelativeTime(new Date(recent.kind === 'note' ? recent.bookmark.createdAt : recent.kind === 'highlight' ? recent.highlight.createdAt : recent.prayer.createdAt).getTime(), t)}
              </span>
            </Link>
          )}

          {BIBLE_READER_V2_ENABLED && families.length > 0 && (
            <section className={styles.meaningSection}>
              <h2 className={styles.sectionHeading}>
                {t('bible.journalLensMeaning')}
              </h2>
              <div className={styles.meaningRow}>
                {families.map((f) => (
                  <Link
                    key={f.family}
                    to={`/bible/bookmarks/meaning/${f.family}`}
                    className={styles.meaningChip}
                  >
                    <span
                      className={styles.meaningDot}
                      style={{ backgroundColor: HL_DOT_HEX[f.family] }}
                      aria-hidden="true"
                    />
                    <span className={styles.meaningText}>
                      <span className={styles.meaningName}>
                        {t(`bible.hlFamily.${f.family}`)}
                      </span>
                      <span className={styles.meaningDesc}>
                        {t(`bible.hlFamilyDesc.${f.family}`)}
                      </span>
                    </span>
                    <span className={styles.meaningCount}>
                      {t('bible.hlFamilyCount', { count: f.count })}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {PRAY_MODE_ENABLED && counts.prayers > 0 && (
            <Link to="/bible/bookmarks/prayed" className={styles.prayedCard}>
              <span className={styles.prayedTitle}>
                {t('bible.journalLensPrayed')}
              </span>
              <span className={styles.prayedCount}>
                {t('bookmark.statPrayers', { count: counts.prayers })}
              </span>
            </Link>
          )}

          <JournalWalk sources={sources} />

          <div className={styles.statRow}>
            {counts.notes > 0 && (
              <span className={styles.stat}>
                {t('bookmark.statNotes', { count: counts.notes })}
              </span>
            )}
            {counts.highlights > 0 && (
              <span className={styles.stat}>
                {t('bookmark.statHighlights', { count: counts.highlights })}
              </span>
            )}
            {counts.prayers > 0 && (
              <span className={styles.stat}>
                {t('bookmark.statPrayers', { count: counts.prayers })}
              </span>
            )}
            {counts.books > 0 && (
              <span className={styles.stat}>
                {t('bookmark.statBooks', { count: counts.books })}
              </span>
            )}
          </div>

          {books.length > 1 && top.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionHeading}>
                {t('bookmark.topBooksHeading')}
              </h2>
              <div className={styles.topGrid}>
                {top.map((b) => (
                  <Link
                    key={b.slug}
                    to={`/bible/bookmarks/book/${b.slug}`}
                    className={styles.topCard}
                    style={{ borderLeftColor: accentFor(b.slug) }}
                  >
                    <span className={styles.topName}>
                      {b.bookTitle ?? b.name}
                    </span>
                    <span className={styles.topCount}>
                      {t('bookmark.bookMarkCount', { count: b.markCount })}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className={styles.section}>
            <h2 className={styles.sectionHeading}>
              {t('bookmark.browseByBookHeading')}
            </h2>
            <ul className={styles.browseList}>
              {books.map((b) => (
                <li key={b.slug} className={styles.rowItem}>
                  <Link
                    to={`/bible/bookmarks/book/${b.slug}`}
                    className={styles.browseRow}
                    style={{ borderLeftColor: accentFor(b.slug) }}
                  >
                    <div className={styles.browseTop}>
                      <span className={styles.browseName}>
                        {b.bookTitle ?? b.name}
                      </span>
                      <span className={styles.browseCount}>
                        {t('bookmark.bookMarkCount', { count: b.markCount })}
                      </span>
                    </div>
                    <span className={styles.intensityTrack} aria-hidden="true">
                      <span
                        className={styles.intensityFill}
                        style={{
                          width: `${Math.max(
                            8,
                            Math.round((b.markCount / maxBookCount) * 100),
                          )}%`,
                          backgroundColor: accentFor(b.slug),
                        }}
                      />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
      </div>
      </div>
    </div>
  );
}
